import React, { useMemo, useState } from 'react';
import { LuBuilding2, LuChevronDown, LuChevronsUpDown, LuChevronUp, LuSearch, LuX } from 'react-icons/lu';

import { GUID, Tenant } from '@ndvr/auth-api';
import { GlobalAccount } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useInternalUserDisplayName } from '@/modules/internal-user-queries';

import {
    useMoveHouseholdMutation,
    useMoveHouseholdsListQuery,
    useMoveHouseholdsTenantsQuery,
} from './move-households-queries';

type SortField = 'householdName' | 'primaryAdvisor' | 'clientBase';
type SortDirection = 'asc' | 'desc';

type PendingChange = {
    globalAccountGUID: GUID<GlobalAccount>;
    householdName: string;
    from: string;
    to: string;
    toTenantGUID: GUID<Tenant>;
};

export const MoveHouseholdsAdmin = () => {
    const { data: tenants = [] } = useMoveHouseholdsTenantsQuery();
    const [selectedTenantGUID, setSelectedTenantGUID] = useState<GUID<Tenant> | null>(null);
    const { data: households = [], isLoading } = useMoveHouseholdsListQuery(selectedTenantGUID);
    const moveHousehold = useMoveHouseholdMutation();
    const getInternalUserName = useInternalUserDisplayName();

    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState<SortField>('householdName');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [pendingTenantChange, setPendingTenantChange] = useState<PendingChange | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const tenantNameByGUID = useMemo(() => new Map(tenants.map((t) => [t.tenantGUID, t.name])), [tenants]);

    const tenantNames = useMemo(() => tenants.map((t) => t.name).sort(), [tenants]);

    const householdRows = useMemo(
        () =>
            households.map((h) => ({
                globalAccountGUID: h.globalAccountGUID,
                householdName: h.globalAccountNamePlain ?? '',
                primaryAdvisor: getInternalUserName(h.primaryAdvisorGUID),
                clientBase: tenantNameByGUID.get(h.tenantGUID) ?? '',
                tenantGUID: h.tenantGUID,
            })),
        [households, tenantNameByGUID, getInternalUserName],
    );

    const filteredSortedHouseholds = useMemo(() => {
        let result = householdRows;

        if (search) {
            const lower = search.toLowerCase();
            result = result.filter(
                (h) => h.householdName.toLowerCase().includes(lower) || h.primaryAdvisor.toLowerCase().includes(lower),
            );
        }

        return [...result].sort((a, b) => {
            const cmp = a[sortField].localeCompare(b[sortField]);
            return sortDirection === 'asc' ? cmp : -cmp;
        });
    }, [householdRows, search, sortField, sortDirection]);

    const handleTenantChange = (value: string) => {
        setSelectedTenantGUID(value);
        setSearch('');
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleAssignTenant = (
        globalAccountGUID: GUID<GlobalAccount>,
        householdName: string,
        currentTenantName: string,
        newTenantName: string,
    ) => {
        const toTenant = tenants.find((t) => t.name === newTenantName);
        if (!toTenant) return;
        setPendingTenantChange({
            globalAccountGUID,
            householdName,
            from: currentTenantName,
            to: newTenantName,
            toTenantGUID: toTenant.tenantGUID,
        });
        setConfirmOpen(true);
    };

    const handleCancelAssign = () => {
        setConfirmOpen(false);
        setPendingTenantChange(null);
    };

    const handleConfirmAssign = () => {
        if (!pendingTenantChange) return;
        moveHousehold.mutate(
            {
                tenantGUID: pendingTenantChange.toTenantGUID,
                globalAccountGUID: pendingTenantChange.globalAccountGUID,
            },
            {
                onSettled: () => {
                    setConfirmOpen(false);
                    setPendingTenantChange(null);
                },
            },
        );
    };

    const SortIcon = ({ field }: { field: SortField }) => {
        if (sortField !== field) return <LuChevronsUpDown className="text-muted-foreground ml-1 h-3 w-3" />;
        return sortDirection === 'asc' ? (
            <LuChevronUp className="ml-1 h-3 w-3" />
        ) : (
            <LuChevronDown className="ml-1 h-3 w-3" />
        );
    };

    return (
        <>
            <Card>
                <CardHeader className="py-4">
                    <CardTitle className="flex items-center gap-2 text-base font-medium">
                        <LuBuilding2 className="h-4 w-4" />
                        Household Client Base Assignments
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                        <Select value={selectedTenantGUID ?? ''} onValueChange={handleTenantChange}>
                            <SelectTrigger className="w-[220px]">
                                <SelectValue placeholder="Select a client base..." />
                            </SelectTrigger>
                            <SelectContent>
                                {tenants.map((t) => (
                                    <SelectItem key={t.tenantGUID} value={t.tenantGUID}>
                                        {t.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="relative max-w-sm min-w-[220px] flex-1">
                            <LuSearch className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                            <Input
                                placeholder="Search households or advisors..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pr-8 pl-9"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch('')}
                                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2"
                                >
                                    <LuX className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="text-muted-foreground text-xs">
                        {!selectedTenantGUID
                            ? 'Select a client base to load households'
                            : isLoading
                              ? 'Loading...'
                              : `${filteredSortedHouseholds.length} of ${householdRows.length} households`}
                    </div>

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>
                                    <button
                                        onClick={() => handleSort('householdName')}
                                        className="hover:text-foreground inline-flex items-center transition-colors"
                                    >
                                        Household <SortIcon field="householdName" />
                                    </button>
                                </TableHead>
                                <TableHead>
                                    <button
                                        onClick={() => handleSort('primaryAdvisor')}
                                        className="hover:text-foreground inline-flex items-center transition-colors"
                                    >
                                        Primary Advisor <SortIcon field="primaryAdvisor" />
                                    </button>
                                </TableHead>
                                <TableHead>
                                    <button
                                        onClick={() => handleSort('clientBase')}
                                        className="hover:text-foreground inline-flex items-center transition-colors"
                                    >
                                        Client Base <SortIcon field="clientBase" />
                                    </button>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredSortedHouseholds.map((household) => (
                                <TableRow key={household.globalAccountGUID}>
                                    <TableCell className="font-medium">{household.householdName}</TableCell>
                                    <TableCell>{household.primaryAdvisor}</TableCell>
                                    <TableCell>
                                        <Select
                                            value={household.clientBase}
                                            onValueChange={(value) =>
                                                handleAssignTenant(
                                                    household.globalAccountGUID,
                                                    household.householdName,
                                                    household.clientBase,
                                                    value,
                                                )
                                            }
                                        >
                                            <SelectTrigger className="w-[200px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {tenantNames.map((base) => (
                                                    <SelectItem key={base} value={base}>
                                                        {base}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!isLoading && filteredSortedHouseholds.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} className="text-muted-foreground py-8 text-center">
                                        {!selectedTenantGUID
                                            ? 'Select a client base to view households'
                                            : 'No households match the current filters'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog
                open={confirmOpen}
                onOpenChange={(open) => {
                    if (!open) handleCancelAssign();
                }}
            >
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Confirm Client Base Change</DialogTitle>
                    </DialogHeader>
                    {pendingTenantChange && (
                        <div className="space-y-3 py-4">
                            <p className="text-muted-foreground text-sm">
                                Are you sure you want to change the client base for{' '}
                                <span className="text-foreground font-medium">{pendingTenantChange.householdName}</span>
                                ?
                            </p>
                            <div className="bg-muted/50 space-y-1 rounded-md border p-3">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">From:</span>
                                    <span className="font-medium">{pendingTenantChange.from}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">To:</span>
                                    <span className="font-medium">{pendingTenantChange.to}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={handleCancelAssign}>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmAssign} disabled={moveHousehold.isPending}>
                            Confirm Change
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
